import { useEffect, useState } from 'react';
import axios from 'axios';

/**
 * Итог дня («Сегодня»): общий кэш для чипа в шапке, Главной, листа дня и сессий.
 *
 * Источник — блок `today` из GET /api/v1/dashboard (+ серия из `user.streak`).
 * Snapshot один на все потребители: Layout берёт из него и счётчик «к повтору»,
 * Главная публикует свой свежий snapshot сюда же — лишних запросов нет.
 * Ответы в тренажёрах (todayXp / goalTarget) подмешиваются сразу, без запроса,
 * а после занятия кэш помечается устаревшим и перечитывается.
 */

const TTL = 60_000;
// plan: undefined — snapshot ещё не приходил; null — плана нет.
let state = { today: null, at: 0, plan: undefined };
let inflight = null;
const listeners = new Set();
const planListeners = new Set();

const emit = () => {
  listeners.forEach((fn) => fn(state.today));
  planListeners.forEach((fn) => fn(state.plan));
};

export const getToday = () => state.today;

/** Свежий snapshot дашборда → кэш итога дня. */
export function publishSnapshot(data) {
  if (!data || !data.today) return;
  const streak = data.user && data.user.streak != null ? Number(data.user.streak) || 0 : state.today?.streak ?? null;
  state = { today: { ...data.today, streak }, at: Date.now(), plan: data.planSummary ?? null };
  emit();
}

/** Точечное обновление (ответ в тренажёре, итог занятия) — без запроса. */
export function patchToday(patch) {
  if (!patch) return;
  const prev = state.today || {};
  const next = { ...prev };
  Object.entries(patch).forEach(([k, v]) => { if (v != null) next[k] = v; });
  if (next.xp != null && next.goal != null) next.done = next.xp >= next.goal;
  state = { ...state, today: next };
  emit();
}

/** Пометить устаревшим: следующий loadDashboard/useToday перечитает. */
export function invalidateToday() {
  state = { ...state, at: 0 };
}

/** GET /api/v1/dashboard с дедупликацией параллельных вызовов. */
export function loadDashboard(force = false) {
  if (!force && state.today && Date.now() - state.at < TTL) return Promise.resolve(null);
  if (inflight) return inflight;
  inflight = axios.get('/api/v1/dashboard')
    .then((res) => { publishSnapshot(res.data); return res.data || null; })
    .catch(() => null)
    .finally(() => { inflight = null; });
  return inflight;
}

/** Подписка на итог дня; при устаревшем кэше — подгружает. */
export function useToday({ load = true } = {}) {
  const [today, setToday] = useState(state.today);
  useEffect(() => {
    listeners.add(setToday);
    setToday(state.today);
    if (load) loadDashboard(false);
    return () => { listeners.delete(setToday); };
  }, [load]);
  return today;
}

/** planSummary из того же snapshot: есть ли план и куда он ведёт. loaded=false — ещё не знаем. */
export function usePlanSummary({ load = true } = {}) {
  const [plan, setPlan] = useState(state.plan);
  useEffect(() => {
    planListeners.add(setPlan);
    setPlan(state.plan);
    if (load) loadDashboard(false);
    return () => { planListeners.delete(setPlan); };
  }, [load]);
  return { loaded: plan !== undefined, summary: plan ?? null };
}

/** Ответы засчитывания несут todayXp/goalTarget — сразу двигаем чип в шапке. */
const ANSWER_URL = /\/api\/(?:v1\/)?(?:(words|sentences)\/[^/]+\/(increment-correct|decrement-correct|review|miss)|speech\/analyze)$/;
export const isAnswerRequest = (config) => (
  String(config?.method || '').toLowerCase() === 'post' && ANSWER_URL.test(String(config?.url || ''))
);

axios.interceptors.response.use((res) => {
  try {
    if (isAnswerRequest(res?.config)) {
      const d = res.data || {};
      if (d.todayXp != null) patchToday({ xp: Number(d.todayXp), goal: d.goalTarget != null ? Number(d.goalTarget) : undefined });
    }
  } catch (_) { /* учёт не должен ломать запрос */ }
  return res;
});
